import { motion } from "framer-motion";
import { WHY_US } from "@/data/content";
import { Hammer, Compass, ShieldCheck, MapPin, FileCheck2, Heart } from "lucide-react";
import usePageContent from "@/hooks/usePageContent";

const ease = [0.16, 1, 0.3, 1];

const POINTS = [
    { icon: Compass, title: "Accompagnement personnalisé", text: "Un interlocuteur unique du premier rendez-vous à la pose finale." },
    { icon: Hammer, title: "Fabrication artisanale", text: "Pièces uniques réalisées en atelier, à la main, avec exigence." },
    { icon: ShieldCheck, title: "Finitions haut de gamme", text: "Matières nobles, ajustements millimétrés, durabilité garantie." },
    { icon: MapPin, title: "Bruxelles & alentours", text: "Interventions à Etterbeek et toute la région bruxelloise." },
    { icon: FileCheck2, title: "Devis gratuit & détaillé", text: "Une étude transparente, sans engagement, livrée sous 7 jours." },
    { icon: Heart, title: "Une approche humaine", text: "Écoute, conseil et respect du lieu — votre projet, notre attention." },
];

export default function WhyUs() {
    const t = usePageContent("home");
    const stats = [
        { n: t("whyus.stat_years", WHY_US[0].n), label: WHY_US[0].label },
        { n: t("whyus.stat_projects", WHY_US[1].n), label: WHY_US[1].label },
        { n: t("whyus.stat_artisan", WHY_US[2].n), label: WHY_US[2].label },
        { n: t("whyus.stat_area", WHY_US[3].n), label: WHY_US[3].label },
    ];
    return (
        <section
            id="pourquoi"
            data-testid="whyus-section"
            className="bg-[#FDFBF7] py-24 md:py-32"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-20">
                    <div className="md:col-span-6">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-[#DC2626]" />
                            <span className="overline">{t("whyus.overline", "03 · Notre savoir faire")}</span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#1E3A5F] leading-[1.05]">
                            {t("whyus.title", "Une exigence artisanale,")}<br />
                            <span className="italic">{t("whyus.title_italic", "un regard d'architecte.")}</span>
                        </h2>
                    </div>
                    <div className="md:col-span-5 md:col-start-8 flex items-end">
                        <p className="text-[#64748B] text-lg leading-relaxed">
                            Depuis plus d'une décennie, RO Concept conçoit et fabrique des
                            espaces sur mesure pour une clientèle exigeante. Notre démarche
                            mêle dessin, matière et précision.
                        </p>
                    </div>
                </div>

                {/* Numbers */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 mb-24 border-y border-[#E2E8F0]/80 py-12">
                    {stats.map((n, i) => (
                        <motion.div
                            key={n.label}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.9, delay: i * 0.1, ease }}
                            data-testid={`stat-${i}`}
                            className="flex flex-col"
                        >
                            <span className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#1E3A5F] leading-none">
                                {n.n}
                            </span>
                            <span className="overline mt-4">{n.label}</span>
                        </motion.div>
                    ))}
                </div>

                {/* Points */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-14">
                    {POINTS.map((p, i) => {
                        const Icon = p.icon;
                        return (
                            <motion.div
                                key={p.title}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.8, delay: i * 0.07, ease }}
                                data-testid={`why-point-${i}`}
                                className="group"
                            >
                                <div className="w-12 h-12 flex items-center justify-center border border-[#1E3A5F]/20 text-[#1E3A5F] mb-6 group-hover:bg-[#1E3A5F] group-hover:text-white transition-colors duration-500">
                                    <Icon size={20} strokeWidth={1.4} />
                                </div>
                                <h3 className="font-serif text-2xl text-[#1E3A5F]">{p.title}</h3>
                                <p className="mt-3 text-[#64748B] leading-relaxed">{p.text}</p>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
